<template>
  <div class="category">
    <div v-if="currentCategory">
      <h1 class="page-title">
        <router-link to="/categories">Categories</router-link>
        <span class="divider">&rsaquo;</span>
        {{ currentCategory.label | unslugify }}
      </h1>

      <div class="page-content -column -column-one -form">
        <category-form
          :state="formState"
          :category="editCategory"
          :button-text="'Update Category'"
          @submitted="updateCategory"
        ></category-form>
      </div>

      <div class="page-content -column -column-two">
        <categorized-messages-list
          :messages="messagesForCategory(currentCategory.id)"
          :category-id="currentCategory.id"
        ></categorized-messages-list>
      </div>
    </div>
    <div v-else>
      <div class="page-content">Loading...</div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import categorizedMessagesList from "@/components/categorizedMessagesList.vue";
import categoryForm from "@/components/categoryForm";
import auth from "@/helpers/auth";

export default {
  components: { categoryForm, categorizedMessagesList },

  beforeRouteEnter(to, from, next) {
    if (!auth.signedIn()) return next("/sign-in");
    next();
  },

  data() {
    return {
      formState: "normal",
      editCategory: {
        id: null,
        label: "",
        weighting: 1,
        keywords: "",
        negative_keywords: "",
        parent_id: "",
        query_id: "",
        max_consecutive: null,
        fallback: false,
        contains_time: false
      }
    };
  },

  computed: {
    ...mapState({
      categories: (state) => state.categories.all,
      currentCategory: (state) => state.categories.current,
      queries: (state) => state.queries.all
    }),
    ...mapGetters(["messagesForCategory", "currentTeam"])
  },

  watch: {
    categories() {
      if (this.categories.length) this.setCurrentCategory();
    },

    currentCategory(category, oldCategory) {
      if (!category) return;
      this.updateFormFields();

      // Category updated
      if (oldCategory && category.id === oldCategory.id) {
        this.formState = "done";
        setTimeout(() => {
          this.formState = "normal";
        }, 1000);

        // Category changed
      } else {
        this.checkFallbackCategory();
        this.checkContainsTimeCategory();
      }
    },

    currentTeam() {
      this.checkFallbackCategory();
      this.checkContainsTimeCategory();
    }
  },

  created() {
    if (this.categories.length) this.setCurrentCategory();
    if (this.currentCategory) this.updateFormFields();
    this.checkFallbackCategory();
    this.checkContainsTimeCategory();
  },

  methods: {
    setCurrentCategory() {
      this.$store.dispatch("changeCurrentCategory", this.$route.params.id);
      this.$store.dispatch("fetchCategorizedMessages", this.$route.params.id).catch((err) => {
        console.error("Error fetching messages", err);
      });
    },

    updateCategory() {
      if (this.formState !== "normal") return;
      this.formState = "loading";

      this.$store.dispatch("updateCategory", this.editCategory).then(() => {
        if (this.editCategory.fallback) {
          this.$store.dispatch("updateTeamFallbackCategoryId", this.currentCategory.id);
        } else if (this.currentCategory.id === this.currentTeam.fallback_category_id) {
          this.$store.dispatch("updateTeamFallbackCategoryId", null);
        }

        if (this.editCategory.contains_time) {
          this.$store.dispatch("updateTeamContainsTimeCategoryId", this.currentCategory.id);
        } else if (this.currentCategory.id === this.currentTeam.contains_time_category_id) {
          this.$store.dispatch("updateTeamContainsTimeCategoryId", null);
        }
      });
    },

    updateFormFields() {
      this.editCategory.id = this.currentCategory.id;
      this.editCategory.label = this.currentCategory.label;
      this.editCategory.weighting = this.currentCategory.weighting;
      this.editCategory.keywords = this.currentCategory.keywords;
      this.editCategory.negative_keywords = this.currentCategory.negative_keywords;
      this.editCategory.parent_id = this.currentCategory.parent_id || "";
      this.editCategory.query_id = this.currentCategory.query_id || "";
      this.editCategory.max_consecutive = this.currentCategory.max_consecutive || null;
    },

    checkFallbackCategory() {
      if (!this.currentTeam || !this.currentCategory) return;
      this.editCategory.fallback = this.currentCategory.id === this.currentTeam.fallback_category_id;
    },

    checkContainsTimeCategory() {
      if (!this.currentTeam || !this.currentCategory) return;
      this.editCategory.contains_time =
        String(this.currentCategory.id) === this.currentTeam.settings.contains_time_category_id;
    }
  }
};
</script>
